import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { StatusCode } from "api/protocols";
import Page from "components/page/Page";
import DataTable from "components/dataTable/DataTable";
import Card from "components/card/Card";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPencil, faPlus } from "@fortawesome/pro-regular-svg-icons";
import "./amenities.scss";
import { useAppDispatch } from "hooks/redux";
import { showError } from "redux/actions/ui";
import {
  GetAccommodationAmenities,
  PostAccommodationAmenity,
  PutAccommodationAmenity,
} from "api/rpc/2024-04/facilityAdmin/accommodation/amenities";
import Sheet from "components/sheet/Sheet";
import Portal from "elements/Portal";
import Input from "components/form/input/Input";
import { capitalize, handleChangeEventInput } from "helpers/Helpers";
import EmojiGrid, { allIcons } from "components/IconSelector/iconSelector";

export interface IAccommodationAmenity {
  id: number;
  title: string;
  icon: string;
}

export function Amenities() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [accommodationAmenities, setAccommodationAmenities] = useState(undefined);

  const [filterState, setFilterState] = useState({
    search: "",
    offset: 0,
    limit: 10,
  });

  useEffect(() => {
    void loadAccommodationAmenities();
  }, [filterState]);

  const [newAmenityState, setNewAmenityState] = useState({
    isOpen: false,
    id: undefined,
    title: "",
    icon: "",
  });

  async function loadAccommodationAmenities() {
    if (accommodationAmenities !== undefined) {
      setAccommodationAmenities(undefined);
    }
    const accommodationAmenitiesRes = await GetAccommodationAmenities(null, false);

    if (accommodationAmenitiesRes.status !== StatusCode.OK) {
      dispatch(showError("Error loading amenities"));

      setAccommodationAmenities([]);
      return;
    }

    setAccommodationAmenities(accommodationAmenitiesRes.data);
  }

  const handleSaveAmenity = async () => {
    if (newAmenityState.id) {
      const res = await PutAccommodationAmenity(
        {
          id: newAmenityState.id,
          title: newAmenityState.title,
          icon: newAmenityState.icon,
        },
        true,
      );
      if (res.status !== StatusCode.OK) {
        dispatch(showError("Error updating amenity"));
        return;
      }
    } else {
      const res = await PostAccommodationAmenity(
        {
          title: newAmenityState.title,
          icon: newAmenityState.icon,
        },
        true,
      );
      if (res.status !== StatusCode.OK) {
        dispatch(showError("Error creating amenity"));
        return;
      }
    }

    setNewAmenityState({ isOpen: false, id: undefined, title: "", icon: "" });
    void loadAccommodationAmenities();
  };

  const handleEditAmenity = (amenity: IAccommodationAmenity) => {
    setNewAmenityState({
      isOpen: true,
      id: amenity.id,
      title: amenity.title,
      icon: amenity.icon || "",
    });
  };

  return (
    <Page className="mt-4">
      <Card
        title="Amenities"
        subtitle="Manage your amenities here"
        titleActions={[
          {
            action: () =>
              setNewAmenityState({
                isOpen: true,
                id: undefined,
                title: "",
                icon: "",
              }),
            content: (
              <>
                <FontAwesomeIcon icon={faPlus} style={{ marginRight: "6px" }} />
                {t("New amenity")}
              </>
            ),
            buttonType: "primary",
          },
        ]}
      >
        <div style={{ marginTop: "24px" }}>
          <DataTable
            noRoundedTop
            columns={[{ label: t("Title") }, { label: t("Icon") }, { label: t("") }]}
            loading={accommodationAmenities === undefined}
            footer={{
              tableLimit: filterState?.limit,
              tableOffset: filterState?.offset,
              handleTableOffset: direction =>
                setFilterState(prev => ({
                  ...prev,
                  offset: direction === "prev" ? prev.offset - prev.limit : prev.offset + prev.limit,
                })),
              disableNextOffset: !accommodationAmenities || !(accommodationAmenities?.length === filterState?.limit),
            }}
          >
            {accommodationAmenities?.map((amenity: IAccommodationAmenity, index: number) => {
              return (
                <tr key={index} onClick={() => handleEditAmenity(amenity)} className="clickable">
                  <td>{capitalize(amenity?.title)}</td>
                  <td>
                    <FontAwesomeIcon icon={allIcons[amenity.icon]} style={{ fontSize: "18px" }} />
                  </td>
                  <td className="text-right">
                    <FontAwesomeIcon icon={faPencil} />
                  </td>
                </tr>
              );
            })}
          </DataTable>
        </div>
      </Card>
      <Portal isMounted={newAmenityState.isOpen}>
        <Sheet
          open={newAmenityState.isOpen}
          size="medium"
          onClose={() => setNewAmenityState(prev => ({ ...prev, isOpen: false }))}
          onCancel={() => setNewAmenityState(prev => ({ ...prev, isOpen: false }))}
          title={newAmenityState.id ? t("Edit Amenity") : t("New Amenity")}
          onOk={handleSaveAmenity}
          okText={t("Save")}
          closable
          okDisabled={newAmenityState.title === "" || newAmenityState.icon === ""}
        >
          <div className="mb-3">
            <Input
              label={t("Title")}
              id="title"
              value={newAmenityState.title}
              onChange={e => handleChangeEventInput(e, newAmenityState, setNewAmenityState)}
              placeholder={t("Enter amenity title")}
            />
          </div>

          <EmojiGrid
            selectedIcon={newAmenityState.icon}
            onIconSelect={icon => setNewAmenityState(prev => ({ ...prev, icon }))}
            initialFilter="amenities"
            excludeIcons={
              accommodationAmenities
                ?.filter((a: IAccommodationAmenity) => a.id !== newAmenityState.id) // Exclude current amenity when editing
                .map((a: IAccommodationAmenity) => a.icon)
                .filter(Boolean) || [] // Remove any null/undefined icons
            }
          />
        </Sheet>
      </Portal>
    </Page>
  );
}
